import {
  CONTENT_TYPE_FIELD,
  CPS_NOTIFICATION_TYPE,
  CpsNotificationProps,
  CpsSelectedDocument,
  CpsUserAllowedActions,
  DOC_ID_FIELD,
  DocumentIdAndCt,
  LockUnlockResponse,
  DeleteDocumentsResponse
} from '@cs-cms/types';

export interface PermissionCounts {
  changed: number;
  skipped: number;
}

export type TranslateFn = (key: string, params?: Record<string, number>) => string;

export function countDocumentsByPermission(
  documents: CpsSelectedDocument[],
  permission: CpsUserAllowedActions
): PermissionCounts {
  const changed = documents.filter(doc => doc.Cps_Permissions.includes(permission)).length;
  return { changed, skipped: documents.length - changed };
}

export function mapSelectedDocumentsToIdAndCt(documents: CpsSelectedDocument[]): DocumentIdAndCt[] {
  return documents.map(doc => ({
    contentTypeName: doc[CONTENT_TYPE_FIELD],
    documentId: doc[DOC_ID_FIELD]
  }));
}

export function buildConfirmDialogMessage(
  translate: TranslateFn,
  mainMessageKey: string,
  skippedMessageKey: string,
  proceedKey: string,
  counts: PermissionCounts
): string {
  const mainPart = translate(mainMessageKey, { changed: counts.changed });
  const skippedPart = counts.skipped !== 0 ? translate(skippedMessageKey, { skipped: counts.skipped }) : '';

  return mainPart + skippedPart + translate(proceedKey);
}

export function showLockUnlockResultToast(
  response: LockUnlockResponse,
  translate: TranslateFn,
  showToast: (options: Omit<CpsNotificationProps, 'show'>) => void,
  partialMessageKey: string,
  successMessageKey: string
): void {
  const changedCount = response.changed.length;
  const skippedCount = response.skipped.length;

  if (skippedCount > 0) {
    showToast({
      type: CPS_NOTIFICATION_TYPE.INFO,
      message: translate(partialMessageKey, { changedCount, skippedCount })
    });
    return;
  }

  showToast({
    type: CPS_NOTIFICATION_TYPE.SUCCESS,
    message: translate(successMessageKey)
  });
}

export function showDeleteResultToast(
  response: DeleteDocumentsResponse,
  translate: TranslateFn,
  showToast: (options: Omit<CpsNotificationProps, 'show'>) => void
): void {
  const deletedCount = response.deleted.length;
  const skippedCount = response.skipped.length;

  if (skippedCount > 0) {
    showToast({
      type: CPS_NOTIFICATION_TYPE.INFO,
      message: translate('custom.toolbar.actions.delete.partialDelete', {
        deletedCount,
        skippedCount
      })
    });
    return;
  }

  showToast({
    type: CPS_NOTIFICATION_TYPE.SUCCESS,
    message: translate('custom.toolbar.actions.delete.successfullDelete')
  });
}

export function documentHasPermission(document: CpsSelectedDocument, permission: CpsUserAllowedActions): boolean {
  return document.Cps_Permissions.includes(permission);
}

export function anyDocumentHasPermission(documents: CpsSelectedDocument[], permission: CpsUserAllowedActions): boolean {
  return documents.some(doc => documentHasPermission(doc, permission));
}
